import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Megaphone, MessageSquare, Users, CheckCircle2, Bot, Play, Pause, Pencil, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { formatSupabaseError, isMissingColumnError } from "@/lib/supabase-error";
import { mapIntegrationToCampaignOption } from "@/lib/campaign-integration-options";
import {
  CAMPAIGN_CHANNEL_LABELS,
  TRAFFIC_SOURCES,
  aggregateCampaignMetrics,
  setAutomationAndCampaignStatus,
  type CampaignMetrics,
} from "@/lib/automation-campaign";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CampaignRow {
  id: string;
  name: string;
  channel: string;
  keywords: string[];
  status: string;
  utm_campaign: string | null;
  utm_source: string | null;
  ad_name: string | null;
  created_at: string;
  automation_id: string;
  integration_id: string | null;
  integrations: {
    id: string;
    provider: string;
    config: Record<string, unknown> | null;
    instance_identifier: string | null;
  } | null;
  automations: { status: string; name: string } | null;
}

interface CampaignWithMetrics extends CampaignRow {
  metrics: CampaignMetrics;
}

interface AutomationCampaignAnalyticsProps {
  workspaceId: string;
}

const statusLabels: Record<string, string> = {
  draft: "Rascunho",
  active: "Ativa",
  paused: "Pausada",
  ended: "Encerrada",
};

const automationStatusLabels: Record<string, string> = {
  draft: "Rascunho",
  active: "Ativa",
  paused: "Pausada",
  archived: "Arquivada",
};

export function AutomationCampaignAnalytics({ workspaceId }: AutomationCampaignAnalyticsProps) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignWithMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    loadCampaigns();
  }, [workspaceId]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      let selectFields =
        "id, name, channel, keywords, status, utm_source, utm_campaign, ad_name, created_at, automation_id, integration_id, automations(status, name)";

      let { data: campaignRows, error: campError } = await (supabase as any)
        .from("automation_campaigns")
        .select(selectFields)
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });

      if (campError && isMissingColumnError(campError, "integration_id")) {
        selectFields =
          "id, name, channel, keywords, status, utm_source, utm_campaign, ad_name, created_at, automation_id, automations(status, name)";
        ({ data: campaignRows, error: campError } = await (supabase as any)
          .from("automation_campaigns")
          .select(selectFields)
          .eq("workspace_id", workspaceId)
          .order("created_at", { ascending: false }));
      }

      if (campError) throw campError;

      const rows = (campaignRows || []) as CampaignRow[];

      const integrationIds = [
        ...new Set(rows.map((r) => r.integration_id).filter((id): id is string => !!id)),
      ];
      const integrationById = new Map<string, CampaignRow["integrations"]>();
      if (integrationIds.length > 0) {
        const { data: integRows } = await supabase
          .from("integrations")
          .select("id, provider, config, instance_identifier")
          .in("id", integrationIds);
        for (const row of integRows || []) {
          integrationById.set(row.id, row as CampaignRow["integrations"]);
        }
      }

      const rowsWithIntegrations = rows.map((row) => ({
        ...row,
        integrations: row.integration_id
          ? integrationById.get(row.integration_id) ?? null
          : null,
      }));
      if (rows.length === 0) {
        setCampaigns([]);
        return;
      }

      const automationIds = rowsWithIntegrations.map((r) => r.automation_id);

      const { data: logs } = await supabase
        .from("automation_execution_log")
        .select("id, automation_id, final_status, conversation_id")
        .eq("workspace_id", workspaceId)
        .in("automation_id", automationIds)
        .gte("created_at", thirtyDaysAgo);

      const execLogs = (logs || []) as Array<{
        id: string;
        automation_id: string;
        final_status: string;
        conversation_id: string;
      }>;

      const logIds = execLogs.map((l) => l.id);

      let steps: Array<{ execution_log_id: string; action_type: string; status: string }> = [];
      if (logIds.length > 0) {
        const { data: stepRows } = await supabase
          .from("automation_execution_steps")
          .select("execution_log_id, action_type, status")
          .eq("workspace_id", workspaceId)
          .in("execution_log_id", logIds);
        steps = (stepRows || []) as typeof steps;
      }

      const { data: handoffRows } = await supabase
        .from("automation_contact_state")
        .select("automation_id")
        .eq("workspace_id", workspaceId)
        .in("automation_id", automationIds)
        .eq("status", "released_to_ai")
        .gte("updated_at", thirtyDaysAgo);

      const handoffCountByAutomation = new Map<string, number>();
      for (const h of handoffRows || []) {
        const aid = (h as { automation_id: string }).automation_id;
        handoffCountByAutomation.set(aid, (handoffCountByAutomation.get(aid) || 0) + 1);
      }

      const withMetrics: CampaignWithMetrics[] = rowsWithIntegrations.map((row) => ({
        ...row,
        metrics: aggregateCampaignMetrics(
          row.automation_id,
          execLogs,
          steps,
          handoffCountByAutomation.get(row.automation_id) || 0,
        ),
      }));

      setCampaigns(withMetrics);
    } catch (err) {
      console.error("Campaign analytics error:", err);
      toast.error(formatSupabaseError(err, "Erro ao carregar campanhas"));
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignStatusChange = async (campaign: CampaignWithMetrics, newStatus: string) => {
    setStatusUpdatingId(campaign.id);
    try {
      const result = await setAutomationAndCampaignStatus(
        supabase,
        campaign.automation_id,
        newStatus,
      );
      if (result.error) {
        toast.error(result.error);
        return;
      }
      toast.success(
        newStatus === "active"
          ? "Campanha ativada — rastreio iniciado."
          : "Campanha pausada.",
      );
      await loadCampaigns();
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleEditAutomation = (automationId: string) => {
    navigate(`/automations?edit=${automationId}`);
  };

  const handleDeleteCampaign = async (campaign: CampaignWithMetrics) => {
    if (!window.confirm(`Excluir a campanha "${campaign.name}"? A automação vinculada também será removida.`)) {
      return;
    }
    setStatusUpdatingId(campaign.id);
    try {
      const { error: campaignError } = await (supabase as any)
        .from("automation_campaigns")
        .delete()
        .eq("id", campaign.id);

      if (campaignError) throw campaignError;

      const { error: automationError } = await supabase
        .from("automations")
        .delete()
        .eq("id", campaign.automation_id);

      if (automationError) throw automationError;

      toast.success("Campanha excluída.");
      await loadCampaigns();
    } catch (err) {
      toast.error(formatSupabaseError(err, "Erro ao excluir campanha"));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <Megaphone className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nenhuma campanha criada ainda.</p>
          <p className="text-xs mt-1">
            Clique em &quot;Nova campanha&quot; ou marque &quot;Registrar como campanha&quot; ao criar uma automação.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Megaphone className="h-5 w-5" /> Campanhas (últimos 30 dias)
      </h3>

      <div className="space-y-4">
        {campaigns.map((c) => {
          const trafficLabel =
            TRAFFIC_SOURCES.find((s) => s.value === c.utm_source)?.label || c.utm_source;
          const integrationLabel = c.integrations
            ? mapIntegrationToCampaignOption(c.integrations).display_name
            : null;
          return (
          <Card key={c.id}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{c.name}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {CAMPAIGN_CHANNEL_LABELS[c.channel] || c.channel}
                    {integrationLabel ? ` · ${integrationLabel}` : ""}
                    {trafficLabel ? ` · ${trafficLabel}` : ""}
                    {c.utm_campaign ? ` · ${c.utm_campaign}` : ""}
                    {c.ad_name ? ` · ${c.ad_name}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{statusLabels[c.status] || c.status}</Badge>
                  <Badge variant="secondary">
                    Automação:{" "}
                    {automationStatusLabels[c.automations?.status ?? "draft"] || c.automations?.status}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {c.keywords.map((kw) => (
                  <Badge key={kw} variant="secondary" className="text-xs">
                    {kw}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Criada em {format(new Date(c.created_at), "dd MMM yyyy", { locale: ptBR })}
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCell
                  icon={Megaphone}
                  label="Disparos"
                  value={c.metrics.triggers}
                />
                <MetricCell
                  icon={CheckCircle2}
                  label="Match"
                  value={`${c.metrics.matched} (${c.metrics.matchRate}%)`}
                />
                <MetricCell
                  icon={Users}
                  label="Contatos únicos"
                  value={c.metrics.uniqueContacts}
                />
                <MetricCell
                  icon={MessageSquare}
                  label="Msgs enviadas"
                  value={c.metrics.messagesSent}
                />
                <MetricCell
                  icon={Bot}
                  label="Handoff IA"
                  value={c.metrics.aiHandoffs}
                />
                <MetricCell
                  icon={CheckCircle2}
                  label="Falhas"
                  value={c.metrics.failed}
                  destructive={c.metrics.failed > 0}
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {(c.status === "draft" || c.status === "paused") && (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={statusUpdatingId === c.id}
                    onClick={() => handleCampaignStatusChange(c, "active")}
                  >
                    {statusUpdatingId === c.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Play className="h-3.5 w-3.5" />
                    )}
                    Ativar campanha
                  </Button>
                )}
                {c.status === "active" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    disabled={statusUpdatingId === c.id}
                    onClick={() => handleCampaignStatusChange(c, "paused")}
                  >
                    {statusUpdatingId === c.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Pause className="h-3.5 w-3.5" />
                    )}
                    Pausar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={() => handleEditAutomation(c.automation_id)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Editar automação
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-destructive hover:text-destructive"
                  disabled={statusUpdatingId === c.id}
                  onClick={() => handleDeleteCampaign(c)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
}

function MetricCell({
  icon: Icon,
  label,
  value,
  destructive,
}: {
  icon: typeof Megaphone;
  label: string;
  value: string | number;
  destructive?: boolean;
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-xs">{label}</span>
      </div>
      <p className={`text-lg font-semibold ${destructive ? "text-destructive" : ""}`}>
        {value}
      </p>
    </div>
  );
}
